import { Card, CardContent, Grid, Stack, Typography, useMediaQuery } from '@mui/material'
import { motion } from "motion/react"
import { FunctionComponent } from 'react'
import gaiaTheme from '../../../root-components/theme';
import { IColorPalatte } from '../shared-services/public-reports.model';
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode';
import { getGaiaReportBodyTextSx, getGaiaReportSectionShellSx } from './gaia-report-web-page-pdf-styles';
import { useReportContentOverride } from '../shared-services/gaia-report-content';

export const GaiaReportWebPageMethodology: FunctionComponent<{
  screenLabels: any,
  reportPeriodCalendarYear: string,
  reportPeriodYear: string,
  colorPalatte: IColorPalatte
}> = ({ reportPeriodCalendarYear, reportPeriodYear, colorPalatte, screenLabels }) => {

  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const pdfExport = usePdfExportMode();

  const heading = useReportContentOverride('methodology.heading');
  const intro1 = useReportContentOverride('methodology.intro1');
  const intro2 = useReportContentOverride('methodology.intro2');
  const scope1Title = useReportContentOverride('methodology.scope1Title');
  const scope1Body = useReportContentOverride('methodology.scope1Body');
  const scope2Title = useReportContentOverride('methodology.scope2Title');
  const scope2Body = useReportContentOverride('methodology.scope2Body');
  const scope3Title = useReportContentOverride('methodology.scope3Title');
  const scope3Body = useReportContentOverride('methodology.scope3Body');

  const cardInitial = pdfExport ? { opacity: 1 } : { opacity: 0 };
  const cardTransition = pdfExport ? { duration: 0 } : undefined;

  return (
    <Stack sx={{
      ...getGaiaReportSectionShellSx({
        backgroundColor: 'white',
        pdfExport,
        webBottomPadding: isMobileModeSm ? 0 : 6,
        webBorderRadius: '40px 40px 0 0',
        overflow: 'hidden',
      }),
      ...(pdfExport ? {} : {
        mt: -5,
        position: 'relative',
        zIndex: 2,
      }),
    }}>
      <Typography variant='h3' sx={{ color: colorPalatte.main, mb: 2 }}>{heading ?? 'Methodology'}</Typography>
      <Typography sx={getGaiaReportBodyTextSx({ color: 'text.secondary', isMobileModeMd, isMobileModeSm, pdfExport, pdfMarginBottom: 2, webMarginBottom: 2 })}>{intro1 ?? `The methodology section outlines the approach and processes used to measure, calculate, and report our GHG emissions for the ${reportPeriodYear} ${reportPeriodCalendarYear}.`}</Typography>
      <Typography sx={getGaiaReportBodyTextSx({ color: 'text.secondary', isMobileModeMd, isMobileModeSm, pdfExport, pdfMarginBottom: 2, webMarginBottom: isMobileModeMd ? 2 : 8 })}>{intro2 ?? 'This methodology adheres to internationally recognised standards, ensuring accuracy, consistency, and transparency in our emissions reporting. This report follows the GHG Protocol Corporate Standard, the most widely used international accounting tool for government and business leaders to understand, quantify, and manage GHG emissions.'}</Typography>
      <div className="chart-block">
        <Stack display='flex' flexDirection='row' justifyContent='space-between' alignItems='center' gap={4} sx={{ height: '100%', mb: pdfExport ? 0 : (isMobileModeMd ? 50 : 28) }}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <motion.div
                style={{ display: 'flex', height: '100%' }}
                initial={cardInitial}
                viewport={{ once: true }}
                whileInView={{ opacity: 1 }}
                transition={cardTransition || { duration: 2, delay: 0.2 }}>
                <Card sx={{ backgroundColor: 'lightGrey.light', height: '100%', boxShadow: 'none', p: isMobileModeMd ? 0.25 : 0.5 }}>
                  <CardContent>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: colorPalatte.main, fontWeight: 600 }}>Scope 1</Typography>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: 'text.primary', mb: isMobileModeMd ? 0.5 : 2 }}>{scope1Title ?? 'Direct Emissions'}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: isMobileModeMd ? '0.85rem' : '1rem' }}>{scope1Body ?? `Direct greenhouse gas emissions from owned or controlled sources, such as company vehicles and on-site fuel combustion. These emissions are directly generated by your ${screenLabels.organisation.singular}'s activities.`}</Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <motion.div
                style={{ display: 'flex', height: '100%' }}
                initial={cardInitial}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={cardTransition || { duration: 2, delay: 0.8 }}>
                <Card sx={{ backgroundColor: 'lightGrey.light', height: '100%', boxShadow: 'none', p: isMobileModeMd ? 0.25 : 0.5 }}>
                  <CardContent>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: colorPalatte.main, fontWeight: 600 }}>Scope 2</Typography>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: 'text.primary', mb: isMobileModeMd ? 0.5 : 2 }}>{scope2Title ?? 'Indirect Emissions - Owned'}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: isMobileModeMd ? '0.85rem' : '1rem' }}>{scope2Body ?? `Indirect emissions from the generation of purchased electricity, steam, heating, and cooling consumed by your ${screenLabels.organisation.singular}. Although these emissions occur of-site, they are a result of your ${screenLabels.organisation.singular} energy use.`}</Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <motion.div
                style={{ display: 'flex', height: '100%' }}
                initial={cardInitial}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={cardTransition || { duration: 2, delay: 1.4 }}>
                <Card sx={{ backgroundColor: 'lightGrey.light', height: '100%', boxShadow: 'none', p: isMobileModeMd ? 0.25 : 0.5 }}>
                  <CardContent>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: colorPalatte.main, fontWeight: 600 }}>Scope 3</Typography>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: 'text.primary', mb: isMobileModeMd ? 0.5 : 2 }}>{scope3Title ?? 'Indirect Emissions - Not Owned'}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: isMobileModeMd ? '0.85rem' : '1rem' }}>{scope3Body ?? `Indirect emissions that occur throughout the lifecyle of your ${screenLabels.organisation.singular}'s activities, including upstream activities like purchased goods and services, as well as downstream activities such as product use and disposal. These emissions are often the largest part of an ${screenLabels.organisation.singular}'s carbon footprint but are not directly controlled by it.`}</Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Stack>
      </div>
    </Stack >
  )
}